'use client';

import { Suspense, useCallback } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { plural } from '@frameio/i18n';
import { useInProgressExportCount } from '@frameio/integrations/premiere/contexts';
import { useUxpHostInfoContext } from '@frameio/integrations/shared/contexts/UxpHostInfoContext';
import { useIntegrationTabs } from '@frameio/integrations/shared/hooks/useIntegrationTabs';
import {
  getValueForHost,
  type IntegrationHostMap,
} from '@frameio/integrations/shared/utils/integrationHostMap';
import { Badge, Box, Tab, TabList, Tabs } from '@frameio/vapor';
import {
  IconAftereffectsComposition,
  IconLayout,
  IconSequence,
} from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/PremiereTabs';

TEXT.DEFINE({
  /** Sequences tab label */
  sequences: 'Sequences',
  /** Compositions tab label */
  compositions: 'Compositions',
  /** Browse tab label */
  browse: 'Browse',
  /** Accessibility label for the in-progress exports badge */
  exportsInProgressLabel: (count: number) =>
    plural(count, {
      one: '1 export in progress',
      other: `${count} exports in progress`,
    }),
});

const EXPORT_TAB_LABELS: IntegrationHostMap<string> = {
  premiere: TEXT.sequences,
  aftereffects: TEXT.compositions,
};

const EXPORT_TAB_ICONS: IntegrationHostMap<
  typeof IconSequence | typeof IconAftereffectsComposition
> = {
  premiere: IconSequence,
  aftereffects: IconAftereffectsComposition,
};

/**
 * The rendered tabs for the Premiere Pro integration app
 */
export const PremiereTabs = () => {
  const router = useRouter();
  const currentPath = usePathname();

  const handleNavigate = useCallback(
    (path: string) => {
      router.push(path);
    },
    [router]
  );

  const hostInfo = useUxpHostInfoContext();
  const exportTabLabel =
    getValueForHost(EXPORT_TAB_LABELS, hostInfo.name) ?? TEXT.sequences;
  const ExportTabIcon = getValueForHost(EXPORT_TAB_ICONS, hostInfo.name);
  const inProgressCount = useInProgressExportCount();

  const {
    activeTab,
    isLoading,
    handleBrowseTabSelection,
    handleExportTabSelection,
  } = useIntegrationTabs({
    currentPath,
    onNavigate: handleNavigate,
  });

  return (
    <Suspense fallback={null}>
      <Tabs defaultValue={activeTab} value={activeTab}>
        <TabList>
          <Tab
            badge={
              <Badge
                backgroundColor="brand"
                content={inProgressCount}
                isShowing={inProgressCount > 0}
                label={TEXT.exportsInProgressLabel(inProgressCount) as string}
                shouldAnimateOnUpdate={false}
              />
            }
            disabled={isLoading}
            onClick={handleExportTabSelection}
          >
            <Box alignItems="center" as="span" display="flex" gap={4}>
              {ExportTabIcon ? <ExportTabIcon size={16} /> : null}
              <span>{exportTabLabel}</span>
            </Box>
          </Tab>
          <Tab disabled={isLoading} onClick={handleBrowseTabSelection}>
            <Box alignItems="center" as="span" display="flex" gap={4}>
              <IconLayout size={16} />
              <span>{TEXT.browse}</span>
            </Box>
          </Tab>
        </TabList>
      </Tabs>
    </Suspense>
  );
};
